import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import axios from 'axios';
import {
  Key,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  RefreshCw,
  FolderPlus,
  Folder,
  Lock,
  Globe,
  Building2,
  X,
  Info
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { Switch } from '../../../../components/ui/switch';
import { Textarea } from '../../../../components/ui/textarea';
import { cn } from '../../../../lib/utils';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const SCOPE_OPTIONS = [
  { value: 'platform_only', label: 'Platform Only', icon: Lock, color: 'purple' },
  { value: 'available_to_all', label: 'Available to All Agencies', icon: Globe, color: 'blue' },
  { value: 'seed_into_new', label: 'Seed Into New Agencies', icon: Building2, color: 'emerald' },
  { value: 'agency_editable', label: 'Agency Editable', icon: Edit, color: 'amber' },
  { value: 'locked', label: 'Locked / Read Only', icon: Lock, color: 'red' },
];

const PlatformCustomValuesPage = () => {
  const { token } = useAuth();
  const [values, setValues] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // all, folders
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    value: '',
    folder_id: null,
    scope: 'platform_only',
    available_to_agencies: false,
    is_locked: false
  });
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [valuesRes, foldersRes] = await Promise.all([
        axios.get(`${API}/platform/settings/data/custom-values`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/platform/settings/data/folders?type=custom_values`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setValues(valuesRes.data.values || []);
      setFolders(foldersRes.data.folders || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load custom values');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.key) {
      toast.error('Name and key are required');
      return;
    }

    try {
      if (editingValue) {
        await axios.put(`${API}/platform/settings/data/custom-values/${editingValue.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Value updated');
      } else {
        await axios.post(`${API}/platform/settings/data/custom-values`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Value created');
      }
      setShowAddModal(false);
      setEditingValue(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save value');
    }
  };

  const handleDelete = async (valueId) => {
    if (!window.confirm('Are you sure you want to delete this value?')) return;
    
    try {
      await axios.delete(`${API}/platform/settings/data/custom-values/${valueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Value deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete value');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName) return;
    
    try {
      await axios.post(`${API}/platform/settings/data/folders`, {
        name: newFolderName,
        type: 'custom_values'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Folder created');
      setShowFolderModal(false);
      setNewFolderName('');
      fetchData();
    } catch (err) {
      toast.error('Failed to create folder');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      key: '',
      value: '',
      folder_id: null,
      scope: 'platform_only',
      available_to_agencies: false,
      is_locked: false
    });
  };

  const openEditModal = (val) => {
    setEditingValue(val);
    setFormData({
      name: val.name,
      key: val.key.replace('{{custom_values.', '').replace('}}', ''),
      value: val.value || '',
      folder_id: val.folder_id,
      scope: val.scope || 'platform_only',
      available_to_agencies: val.available_to_agencies,
      is_locked: val.is_locked
    });
    setShowAddModal(true);
  };

  const filteredValues = values.filter(val => {
    const matchesSearch = val.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          val.key.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFolder = !selectedFolder || val.folder_id === selectedFolder;
    return matchesSearch && matchesFolder;
  });

  const getScopeConfig = (scope) => {
    return SCOPE_OPTIONS.find(s => s.value === scope) || SCOPE_OPTIONS[0];
  };

  const getFolderName = (folderId) => {
    const folder = folders.find(f => f.id === folderId);
    return folder?.name || '-';
  };

  return (
    <div className="min-h-screen bg-[#0B1F18] p-8" data-testid="platform-custom-values-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
            <Key className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Custom Values</h1>
            <p className="text-[#93AE9F] text-sm">
              Define reusable variables for platform templates, automations, and system emails
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowFolderModal(true)}
            className="border-zinc-600 text-zinc-300"
          >
            <FolderPlus className="w-4 h-4 mr-2" />
            Add Folder
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setEditingValue(null);
              setShowAddModal(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="add-value-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Custom Value
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 mb-6 border-b border-[rgba(142,218,182,0.12)]">
        <button
          onClick={() => { setActiveTab('all'); setSelectedFolder(null); }}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
            activeTab === 'all'
              ? 'border-amber-500 text-amber-400'
              : 'border-transparent text-[#93AE9F] hover:text-white'
          )}
        >
          All Values
        </button>
        <button
          onClick={() => setActiveTab('folders')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
            activeTab === 'folders'
              ? 'border-amber-500 text-amber-400'
              : 'border-transparent text-[#93AE9F] hover:text-white'
          )}
        >
          Folders
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <Input
            placeholder="Search values..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-700 text-white"
          />
        </div>
        {selectedFolder && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedFolder(null)}
            className="border-zinc-600 text-zinc-400"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Filter
          </Button>
        )}
      </div>

      {activeTab === 'folders' && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {folders.map((folder) => {
            const count = values.filter(v => v.folder_id === folder.id).length;
            return (
              <button
                key={folder.id}
                onClick={() => { setSelectedFolder(folder.id); setActiveTab('all'); }}
                className="p-4 bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl hover:border-emerald-500/30 transition-colors text-left"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Folder className="w-5 h-5 text-amber-400" />
                  <span className="font-medium text-white">{folder.name}</span>
                </div>
                <p className="text-sm text-[#93AE9F]">{count} values</p>
              </button>
            );
          })}
          {folders.length === 0 && (
            <div className="col-span-4 text-center py-8">
              <Folder className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-[#93AE9F]">No folders yet. Create one to organize your values.</p>
            </div>
          )}
        </div>
      )}

      {/* Values Table */}
      {activeTab === 'all' && (
        <div className="bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : filteredValues.length === 0 ? (
            <div className="text-center py-12">
              <Key className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Custom Values</h3>
              <p className="text-[#93AE9F] text-sm mb-4">
                Create custom values to use in platform templates and automations.
              </p>
              <Button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Value
              </Button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-zinc-900/30">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Folder</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Key</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Value</th>
                  <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(142,218,182,0.08)]">
                {filteredValues.map((val) => (
                  <tr key={val.id} className="hover:bg-zinc-900/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-amber-400" />
                        <span className="font-medium text-white">{val.name}</span>
                        {val.is_system_default && (
                          <Badge className="bg-purple-500/20 text-purple-400 border-0 text-xs">System</Badge>
                        )}
                        {val.is_locked && (
                          <Lock className="w-3 h-3 text-zinc-500" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#93AE9F]">
                      {getFolderName(val.folder_id)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-zinc-800 text-emerald-400 px-2 py-1 rounded">
                          {val.key}
                        </code>
                        <button
                          onClick={() => copyToClipboard(val.key)}
                          className="p-1 hover:bg-zinc-700 rounded"
                        >
                          <Copy className="w-3 h-3 text-zinc-500" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-white max-w-xs truncate">
                      {val.value || <span className="text-zinc-500 italic">No value</span>}
                    </td>
                    <td className="px-6 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
                          <DropdownMenuItem
                            onClick={() => openEditModal(val)}
                            className="text-zinc-300 hover:text-white focus:text-white"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => copyToClipboard(val.key)}
                            className="text-zinc-300 hover:text-white focus:text-white"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy Key
                          </DropdownMenuItem>
                          {!val.is_system_default && (
                            <DropdownMenuItem
                              onClick={() => handleDelete(val.id)}
                              className="text-red-400 hover:text-red-300 focus:text-red-300"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      
      {activeTab === 'all' && (
        <div className="mt-4 text-sm text-[#93AE9F]">
          Showing {filteredValues.length} of {values.length} values
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-400 mb-1">Using Custom Values</h4>
            <p className="text-sm text-blue-300/80 mb-2">
              Custom values can be used in Platform Automations, Notification Templates, System Alerts, 
              and email templates. Use the variable key format <code className="bg-blue-900/50 px-1 rounded">{"{{custom_values.key_name}}"}</code>.
            </p>
            <p className="text-sm text-blue-300/80">
              Example: Use <code className="bg-blue-900/50 px-1 rounded">{"{{custom_values.platform_name}}"}</code> to 
              insert "FundElite" in your emails.
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Value Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingValue ? 'Edit Custom Value' : 'Add Custom Value'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Platform Support Email"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Key *</label>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-sm">{"{{custom_values."}</span>
                <Input
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  placeholder="e.g., support_email"
                  className="bg-zinc-800 border-zinc-700 text-white flex-1"
                  disabled={!!editingValue}
                />
                <span className="text-zinc-500 text-sm">{"}}"}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-1">Used as the variable key in templates</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Value</label>
              <Textarea
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="Enter the value..."
                className="bg-zinc-800 border-zinc-700 text-white"
                rows={3}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Folder</label>
              <Select
                value={formData.folder_id || 'none'}
                onValueChange={(v) => setFormData({ ...formData, folder_id: v === 'none' ? null : v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select folder (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="none">No Folder</SelectItem>
                  {folders.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>{folder.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Scope</label>
              <Select
                value={formData.scope}
                onValueChange={(v) => setFormData({ ...formData, scope: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {SCOPE_OPTIONS.map((scope) => (
                    <SelectItem key={scope.value} value={scope.value}>{scope.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-white">Locked</label>
                  <p className="text-xs text-zinc-500">Agencies cannot modify this value</p>
                </div>
                <Switch
                  checked={formData.is_locked}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_locked: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-white">Available to Agencies</label>
                  <p className="text-xs text-zinc-500">Agencies can use this value in their templates</p>
                </div>
                <Switch
                  checked={formData.available_to_agencies}
                  onCheckedChange={(checked) => setFormData({ ...formData, available_to_agencies: checked })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setEditingValue(null);
                resetForm();
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {editingValue ? 'Save Changes' : 'Create Value'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Folder Modal */}
      <Dialog open={showFolderModal} onOpenChange={setShowFolderModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <label className="block text-sm font-medium text-zinc-400 mb-2">Folder Name</label>
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="e.g., System Emails"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowFolderModal(false);
                setNewFolderName('');
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Create Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformCustomValuesPage;
